import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Plus, Trash2, Eye, EyeOff, Check, X, Loader2, ArrowRight, Star, Sparkles, ArrowRightLeft, MessageSquare, BadgeCheck } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, api, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [annonces, setAnnonces] = useState([]);
  const [matches, setMatches] = useState([]);
  const [exchangesSent, setExchangesSent] = useState([]);
  const [exchangesReceived, setExchangesReceived] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [annoncesRes, matchesRes, exchangesSentRes, exchangesReceivedRes, transactionsRes] = await Promise.all([
        api.get('/my-annonces'),
        api.get('/my-matches'),
        api.get('/exchanges?role=sent'),
        api.get('/exchanges?role=received'),
        api.get('/credits/transactions'),
      ]);

      setAnnonces(annoncesRes.data);
      setMatches(matchesRes.data);
      setExchangesSent(exchangesSentRes.data);
      setExchangesReceived(exchangesReceivedRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnnonceToggle = async (annonceId, active) => {
    try {
      await api.put(`/annonces/${annonceId}`, { active: !active });
      toast.success(active ? 'Annonce desactivee' : 'Annonce activee');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAnnonceDelete = async (annonceId) => {
    try {
      await api.delete(`/annonces/${annonceId}`);
      toast.success('Annonce supprimee');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExchangeAction = async (exchangeId, status) => {
    try {
      await api.put(`/exchanges/${exchangeId}`, { status });
      toast.success(
        status === 'accepted' ? 'Proposition acceptée !' :
        status === 'declined' ? 'Proposition refusée' :
        status === 'completed' ? 'Échange marqué comme terminé !' :
        status === 'cancelled' ? 'Proposition annulée' : 'Statut mis à jour'
      );
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      declined: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    const labels = {
      pending: 'En attente',
      accepted: 'Acceptée',
      declined: 'Refusée',
      completed: 'Terminée',
      cancelled: 'Annulée',
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const pendingReceivedCount = exchangesReceived.filter(e => e.status === 'pending').length;

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Tableau de bord</h1>
            <p className="text-muted-foreground">
              Bienvenue, {user?.full_name}
            </p>
          </div>
          <Link to="/annonces/new">
            <Button className="btn-primary" data-testid="new-annonce-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle annonce
            </Button>
          </Link>
        </div>

        {/* Credits Card */}
        <Card className="mb-8 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 text-white overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 mb-1">Mon solde</p>
                <div className="flex items-center gap-3">
                  <Clock className="w-10 h-10" />
                  <span className="text-5xl font-bold">{user?.credits_balance || 0}</span>
                  <span className="text-xl">crédits</span>
                </div>
                <p className="text-white/80 mt-2">
                  {user?.total_exchanges || 0} échange(s) réalisé(s)
                </p>
              </div>
              <div className="hidden md:block">
                <p className="text-sm text-white/80 mb-2">1 crédit = 1 heure de service</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="annonces" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 rounded-xl">
            <TabsTrigger value="annonces">Mes annonces</TabsTrigger>
            <TabsTrigger value="matches">
              <Sparkles className="w-4 h-4 mr-1" />
              Matchs
            </TabsTrigger>
            <TabsTrigger value="received" className="relative">
              Reçues
              {pendingReceivedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                  {pendingReceivedCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent">Envoyées</TabsTrigger>
            <TabsTrigger value="transactions">Historique</TabsTrigger>
          </TabsList>

          {/* My Annonces */}
          <TabsContent value="annonces" className="space-y-4">
            {annonces.length > 0 ? (
              annonces.map((annonce) => (
                <Card key={annonce.id} className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-2xl">
                        {annonce.offer_category?.icon || '✨'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <Link
                              to={`/annonces/${annonce.id}`}
                              className="font-semibold hover:text-primary transition-colors"
                            >
                              {annonce.title}
                            </Link>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {annonce.description}
                            </p>
                          </div>
                          <Badge variant={annonce.active ? 'default' : 'secondary'}>
                            {annonce.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className="bg-primary text-xs">
                            Propose: {annonce.offer_category?.name}
                          </Badge>
                          {annonce.open_to_all ? (
                            <Badge variant="secondary" className="text-xs">Ouvert à tout</Badge>
                          ) : annonce.want_categories_details?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              Cherche: {annonce.want_categories_details.slice(0, 2).map(c => c.name).join(', ')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-3">
                          <Link to={`/annonces/${annonce.id}/matches`}>
                            <Button variant="outline" size="sm" className="rounded-full text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Voir les matchs
                            </Button>
                          </Link>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {annonce.views_count || 0} vues
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleAnnonceToggle(annonce.id, annonce.active)}
                        >
                          {annonce.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette annonce ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irreversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleAnnonceDelete(annonce.id)}
                                className="bg-destructive text-destructive-foreground"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="rounded-xl">
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">Vous n&apos;avez pas encore d&apos;annonce</p>
                  <Link to="/annonces/new">
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer ma première annonce
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Matches */}
          <TabsContent value="matches" className="space-y-4">
            {matches.length > 0 ? (
              matches.map((match) => (
                <Card key={match.id} className={`rounded-xl ${match.is_perfect_match ? 'border-2 border-secondary' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={match.user?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {match.user?.full_name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Link
                            to={`/users/${match.user?.id}`}
                            className="font-semibold hover:text-primary transition-colors"
                          >
                            {match.user?.full_name}
                          </Link>
                          {match.user?.email_verified && (
                            <BadgeCheck className="w-4 h-4 text-primary" />
                          )}
                          {match.is_perfect_match && (
                            <Badge className="bg-secondary text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Match parfait
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium">{match.annonce?.title}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {match.match_reasons?.slice(0, 3).map((reason, i) => (
                            <span key={i} className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">{match.score}</div>
                        <div className="text-xs text-muted-foreground mb-2">points</div>
                        <Link to={`/annonces/${match.id}/propose`}>
                          <Button size="sm" className="btn-primary rounded-full text-xs">
                            <ArrowRightLeft className="w-3 h-3 mr-1" />
                            Proposer
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="rounded-xl">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Aucun match pour le moment</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Creez une annonce pour decouvrir vos matchs !
                  </p>
                  <Link to="/annonces/new">
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une annonce
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Received Exchanges */}
          <TabsContent value="received" className="space-y-4">
            {exchangesReceived.length > 0 ? (
              exchangesReceived.map((exchange) => (
                <Card key={exchange.id} className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={exchange.requester?.avatar_url} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {exchange.requester?.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{exchange.annonce?.title}</p>
                          <p className="text-sm text-muted-foreground">
                            de {exchange.requester?.full_name}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {exchange.duration_hours}h
                            </span>
                            <span className="credit-badge">
                              {exchange.credits_amount} credit(s)
                            </span>
                          </div>
                          {exchange.message && (
                            <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded-lg">
                              &quot;{exchange.message}&quot;
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(exchange.status)}
                        {exchange.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-destructive"
                              onClick={() => handleExchangeAction(exchange.id, 'declined')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="rounded-full bg-primary"
                              onClick={() => handleExchangeAction(exchange.id, 'accepted')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {exchange.status === 'accepted' && (
                          <Button
                            size="sm"
                            className="btn-primary text-xs"
                            onClick={() => handleExchangeAction(exchange.id, 'completed')}
                          >
                            Marquer terminé
                          </Button>
                        )}
                        {exchange.status === 'completed' && (
                          <Link to={`/exchanges/${exchange.id}/review`}>
                            <Button size="sm" variant="outline" className="rounded-full text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Laisser un avis
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="rounded-xl">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune proposition recue</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Sent Exchanges */}
          <TabsContent value="sent" className="space-y-4">
            {exchangesSent.length > 0 ? (
              exchangesSent.map((exchange) => (
                <Card key={exchange.id} className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={exchange.receiver?.avatar_url} />
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {exchange.receiver?.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{exchange.annonce?.title}</p>
                          <p className="text-sm text-muted-foreground">
                            a {exchange.receiver?.full_name}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {exchange.duration_hours}h
                            </span>
                            <span className="credit-badge">
                              {exchange.credits_amount} credit(s)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(exchange.status)}
                        {exchange.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-destructive text-xs"
                            onClick={() => handleExchangeAction(exchange.id, 'cancelled')}
                          >
                            Annuler
                          </Button>
                        )}
                        {exchange.status === 'completed' && (
                          <Link to={`/exchanges/${exchange.id}/review`}>
                            <Button size="sm" variant="outline" className="rounded-full text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Laisser un avis
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="rounded-xl">
                <CardContent className="p-8 text-center">
                  <ArrowRightLeft className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Aucune proposition envoyée</p>
                  <Link to="/annonces">
                    <Button variant="outline" className="rounded-full">
                      Explorer les annonces
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Transactions */}
          <TabsContent value="transactions" className="space-y-4">
            {transactions.length > 0 ? (
              transactions.map((tx) => {
                const isReceived = tx.to_user_id === user?.id;
                return (
                  <Card key={tx.id} className="rounded-xl">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isReceived ? 'bg-green-100 text-green-600 dark:bg-green-900/30' : 'bg-red-100 text-red-600 dark:bg-red-900/30'
                          }`}>
                            <Clock className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{tx.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(tx.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold text-lg ${isReceived ? 'text-green-600' : 'text-red-600'}`}>
                          {isReceived ? '+' : '-'}{tx.amount}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="rounded-xl">
                <CardContent className="p-8 text-center">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune transaction</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
